import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type ReactNode } from "react";
import { cn } from "../lib/utils";
import { _BaseButton, type _BaseButtonProps } from "./_BaseButton";

const actionChipVariants = cva(
	[
		"inline-flex w-fit max-w-full cursor-pointer select-none items-center gap-1 rounded-full border text-left transition-colors",
		"border-bc-default bg-surface-base font-medium text-secondary",
		"enabled:hover:border-brand-accent enabled:hover:text-brand-accent",
		"focus-visible:outline-none focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30 focus-visible:ring-offset-0",
		"disabled:cursor-not-allowed disabled:opacity-[0.45]",
	],
	{
		variants: {
			size: {
				sm: "px-[9px] py-[3px] text-[12px] leading-[18px]",
				md: "px-3 py-1 text-[13px] leading-[20px]",
			},
		},
		defaultVariants: { size: "sm" },
	},
);

type ActionChipVariantProps = VariantProps<typeof actionChipVariants>;

export type ActionChipProps = Omit<_BaseButtonProps, "color" | "type"> &
	ActionChipVariantProps & {
		/**
		 * Optional glyph before the label. Pair `<Icon size="micro" />` with `size="sm"` and
		 * `<Icon size="mini" />` with `size="md"`.
		 */
		leadingIcon?: ReactNode;
	};

/**
 * The low-emphasis pill action — an outlined chip that carries a whole phrase and tones to the
 * accent color on hover. Use it for offered next steps the reader can take or ignore: suggested
 * prompts, quick replies, saved filters.
 *
 * Reach for `Button` when the action is a call to action that needs chrome and weight, for
 * `ClickableText` when the action reads as inline copy, and for `RadioChip` when the chips are a
 * mutually exclusive answer rather than an action.
 */
export const ActionChip: FC<ActionChipProps> = ({
	size,
	leadingIcon,
	className,
	children,
	...rest
}) => (
	<_BaseButton className={cn(actionChipVariants({ size }), className)} {...rest}>
		{leadingIcon}
		{children}
	</_BaseButton>
);
ActionChip.displayName = "ActionChip";
